import { get } from 'svelte/store';
import { projectStructureStore } from './stores.js';

type PriorityMaps = {
  refPriority: Map<string, number>;
  typePriority: Map<string, number>;
};

type GetDisplayNameOptions = {
  grouperStructure?: any;
};

export function getGrouperIds(grouperRow: any): string[] {
  if (!grouperRow) {
    return [];
  }

  const identifiers = new Set<string>();

  if (grouperRow.grouperId) {
    identifiers.add(String(grouperRow.grouperId));
  }

  if (Array.isArray(grouperRow.levels)) {
    grouperRow.levels.forEach((level: any) => {
      if (!Array.isArray(level.refs)) return;
      level.refs.forEach((ref: any) => {
        if (typeof ref === 'string' && ref.trim() !== '') {
          identifiers.add(ref);
        }
      });
    });
  }

  return Array.from(identifiers);
}

export function getCustomValue(row: any): string {
  if (!row) return '';

  if (Array.isArray(row.levels)) {
    const customLevel = row.levels.find((lvl: any) => {
      const identifier = String(lvl.level ?? lvl.number ?? '').toLowerCase();
      return identifier === 'custom';
    });
    if (typeof customLevel?.value === 'string' && customLevel.value.trim()) {
      return customLevel.value.trim();
    }
  }

  return '';
}

export function getDisplayName(grouperRow: any, studyExtract: any, options: GetDisplayNameOptions = {}): string {
  const grouperStructure = resolveGrouperStructure(grouperRow, studyExtract, options.grouperStructure);
  if (!grouperStructure) {
    // Return "Study" when no grouper structure is available
    return 'Study';
  }

  const priorityMaps = createPriorityMaps(grouperStructure);
  const parts: string[] = [];

  (grouperStructure.levels || []).forEach((levelStructure: any, index: number) => {
    const levelNumber = String(levelStructure?.level ?? levelStructure?.number ?? index + 1);
    const levelKey = normalizeLevelKey(levelNumber);

    if (index > 0 || levelKey.toLowerCase() === 'custom') {
      return;
    }

    const rowLevel = Array.isArray(grouperRow?.levels)
      ? grouperRow.levels.find(
          (lvl: any) => String(lvl.level ?? '') === levelNumber
        )
      : null;

    const referenceSources = (levelStructure?.sources || []).filter(
      (source: any) => source?.type === 'reference'
    );
    const levelItems: Array<{ value: string; priority: number; order: number }> = [];

    referenceSources.forEach((source: any, sourceIndex: number) => {
      const refs = Array.isArray(rowLevel?.refs) ? rowLevel.refs : [];
      const refId = refs[sourceIndex];
      if (!refId) return;

      const resolvedValue = resolveReference(source.refTable, refId, studyExtract);
      if (!resolvedValue || resolvedValue === '-') {
        return;
      }

      let priority = 100 + sourceIndex;

      const refPriority = priorityMaps.refPriority.get(`${levelKey}:${String(source.refTable || '').toLowerCase()}`);
      const typePriority = priorityMaps.typePriority.get(`${levelKey}:${String(source.refType || '').toLowerCase()}`);

      if (refPriority !== undefined) {
        priority = refPriority;
      } else if (typePriority !== undefined) {
        priority = typePriority;
      }

      levelItems.push({
        value: resolvedValue,
        priority,
        order: sourceIndex
      });
    });

    if (levelItems.length > 0) {
      levelItems
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }
          return a.order - b.order;
        })
        .forEach((item) => {
          parts.push(item.value);
        });
    }
  });

  const customValue = getCustomValue(grouperRow);
  if (customValue) {
    parts.push(customValue);
  }

  if (parts.length === 0) {
    // Return "Study" instead of study name when there are no references and no custom value
    return 'Study';
  }

  return parts.join(' ● ');
}

function resolveStudyFallback(studyExtract: any): string {
  // Return "Study" as the fallback instead of the study name
  // This matches the requirement that groups without references should show "Study"
  return 'Study';
}

function resolveReference(refTable: string, refID: string, studyExtract: any): string {
  if (!studyExtract?.references) return refID;

  const collection = studyExtract.references.find((ref: any) => ref.id === refTable);
  if (!collection) return refID;

  // Get the reference structure to find the nameRef field
  const structure = get(projectStructureStore);
  const refStructure = structure?.references?.find((ref: any) => ref.id === refTable);
  const nameRef = refStructure?.nameRef;

  if (collection.rows) {
    const row = collection.rows.find((r: any) => r.id === refID);
    if (row?.cells) {
      // Use nameRef if specified, otherwise fall back to 'name' or 'title'
      let nameCell;
      if (nameRef) {
        // Use the nameRef field specified in the reference structure
        nameCell = row.cells.find((c: any) => c.id === nameRef);
      } else {
        // Fallback to 'name' or 'title' for backward compatibility
        nameCell = row.cells.find((c: any) => c.id === 'name' || c.id === 'title');
      }
      if (nameCell?.value) {
        return nameCell.value;
      }
    }
  }

  if (collection.values) {
    const value = collection.values.find((v: any) => v.id === refID);
    if (value?.value) {
      return value.value;
    }
  }

  return refID;
}

function resolveGrouperStructure(grouperRow: any, studyExtract: any, providedStructure?: any): any {
  if (providedStructure) {
    return providedStructure;
  }

  if (!studyExtract?.topics || !Array.isArray(studyExtract.topics)) {
    return null;
  }

  const topicForRow = studyExtract.topics.find((topic: any) => {
    if (!Array.isArray(topic?.grouper)) {
      return false;
    }
    return topic.grouper.some((row: any) => row === grouperRow || row?.grouperId === grouperRow?.grouperId);
  });

  if (!topicForRow?.id) {
    return null;
  }

  const structure = get(projectStructureStore);
  return structure?.topics?.find((topic: any) => topic.id === topicForRow.id)?.grouper || null;
}

export function getLevelRefs(row: any, levelNumber: string | number): string[] {
  if (!Array.isArray(row?.levels)) {
    return [];
  }

  const target = row.levels.find(
    (lvl: any) => String(lvl.level ?? lvl.number ?? '') === String(levelNumber)
  );

  if (!target || !Array.isArray(target.refs)) {
    return [];
  }

  return target.refs.map((ref: any) => (typeof ref === 'string' ? ref : ''));
}

export function getLevelDisplayName(
  row: any,
  levelStructure: any,
  studyExtract: any
): string {
  if (!levelStructure) return '';

  const levelNumber = String(levelStructure?.level ?? levelStructure?.number ?? '');
  const refs = getLevelRefs(row, levelNumber);
  const labels: string[] = [];

  (levelStructure?.sources || [])
    .filter((source: any) => source?.type === 'reference')
    .forEach((source: any, index: number) => {
      const refId = refs[index];
      if (!refId) return;
      labels.push(resolveReference(source.refTable, refId, studyExtract));
    });

  if (labels.length === 0 && levelNumber.toLowerCase() === 'custom') {
    const customValue = getCustomValue(row);
    if (customValue) {
      labels.push(customValue);
    }
  }

  return labels.join(' ● ');
}

function createPriorityMaps(grouperStructure: any): PriorityMaps {
  const refPriority = new Map<string, number>();
  const typePriority = new Map<string, number>();

  if (!grouperStructure?.levels || !Array.isArray(grouperStructure.levels)) {
    return { refPriority, typePriority };
  }

  grouperStructure.levels.forEach((level: any) => {
    const levelKey = normalizeLevelKey(level?.level);
    let order = 0;

    (level?.sources || []).forEach((source: any) => {
      const priority = order++;

      if (source?.type === 'reference') {
        if (source.refTable) {
          refPriority.set(`${levelKey}:${String(source.refTable).toLowerCase()}`, priority);
        }
        if (source.refType) {
          typePriority.set(`${levelKey}:${String(source.refType).toLowerCase()}`, priority);
        }
      } else if (source?.type === 'custom') {
        const customKey = `${levelKey}:${String(source.refType || 'custom').toLowerCase()}`;
        typePriority.set(customKey, priority);
      }
    });
  });

  return { refPriority, typePriority };
}

function normalizeLevelKey(levelNumber: any): string {
  if (levelNumber === undefined || levelNumber === null) {
    return 'default';
  }
  return String(levelNumber);
}

export function ensureGrouperArray(grouper: any, context: string): any[] {
  if (!Array.isArray(grouper)) {
    throw new Error(`[grouper] Expected grouper array for ${context}`);
  }
  return grouper;
}

export function findGrouperRow(
  grouperRows: any[],
  rowId: string,
  fallbackIndex?: number
): any {
  ensureGrouperArray(grouperRows, `finding row "${rowId}"`);

  let match = grouperRows.find((row: any) => String(row?.grouperId ?? '') === String(rowId));

  if (!match) {
    match = grouperRows.find((row: any) => getGrouperIds(row).includes(String(rowId)));
  }

  if (!match && fallbackIndex !== undefined && fallbackIndex >= 0 && fallbackIndex < grouperRows.length) {
    match = grouperRows[fallbackIndex];
  }

  if (!match) {
    throw new Error(`[grouper] Row "${rowId}" not found`);
  }

  return match;
}

